import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { startOfDay, endOfDay, subDays } from "date-fns";
import { useBranchContext } from "@/hooks/useBranchContext";
import { useOrganizationContext } from "@/hooks/useOrganizationContext";

interface ExtendedStats {
  patientsMarked: number;
  plannedDischarge: number;
  losCarryOver: number;
  escalationsL1: number;
  escalationsL2: number;
  escalationsL3: number;
  admittedToday: number;
  admittedYesterday: number;
}

export const useDashboardExtendedStats = () => {
  const { currentBranchId } = useBranchContext();
  const { organizationId } = useOrganizationContext();
  return useQuery({
    queryKey: ["dashboard-extended-stats", organizationId, currentBranchId],
    queryFn: async (): Promise<ExtendedStats> => {
      if (currentBranchId == null || !organizationId) {
        return { patientsMarked: 0, plannedDischarge: 0, losCarryOver: 0, escalationsL1: 0, escalationsL2: 0, escalationsL3: 0, admittedToday: 0, admittedYesterday: 0 };
      }
      const today = new Date();
      const todayStart = startOfDay(today).toISOString();
      const todayEnd = endOfDay(today).toISOString();
      const yesterdayStart = startOfDay(subDays(today, 1)).toISOString();
      const yesterdayEnd = endOfDay(subDays(today, 1)).toISOString();

      const { data: marked } = await supabase
        .from("activities").select("id").eq("workflow_step", "MD").eq("status", "completed")
        .eq("branch_id", currentBranchId).eq("organization_id", organizationId)
        .gte("completion_time", todayStart).lte("completion_time", todayEnd);

      const todayDate = today.toISOString().split("T")[0];
      const { data: planned } = await supabase
        .from("patients").select("id").eq("branch_id", currentBranchId).eq("organization_id", organizationId)
        .gte("expected_discharge", todayDate + "T00:00:00").lte("expected_discharge", todayDate + "T23:59:59");

      const { data: carryOver } = await supabase
        .from("patients").select("id").eq("branch_id", currentBranchId).eq("organization_id", organizationId)
        .lt("expected_discharge", todayStart).eq("initiated", true).neq("discharge_status", "discharged");

      const { data: alerts } = await supabase
        .from("tat_alert_logs").select("escalation_level")
        .eq("branch_id", currentBranchId).eq("organization_id", organizationId)
        .gte("created_at", todayStart).lte("created_at", todayEnd);

      const escalationsL1 = alerts?.filter(a => a.escalation_level === 1).length || 0;
      const escalationsL2 = alerts?.filter(a => a.escalation_level === 2).length || 0;
      const escalationsL3 = alerts?.filter(a => a.escalation_level === 3).length || 0;

      const { data: admittedToday } = await supabase
        .from("patients").select("id").eq("branch_id", currentBranchId).eq("organization_id", organizationId)
        .gte("admission_datetime", todayStart).lte("admission_datetime", todayEnd);

      const { data: admittedYesterday } = await supabase
        .from("patients").select("id").eq("branch_id", currentBranchId).eq("organization_id", organizationId)
        .gte("admission_datetime", yesterdayStart).lte("admission_datetime", yesterdayEnd);

      return {
        patientsMarked: marked?.length || 0,
        plannedDischarge: planned?.length || 0,
        losCarryOver: carryOver?.length || 0,
        escalationsL1, escalationsL2, escalationsL3,
        admittedToday: admittedToday?.length || 0,
        admittedYesterday: admittedYesterday?.length || 0,
      };
    },
    enabled: currentBranchId != null && !!organizationId,
    refetchInterval: 30000,
  });
};
